'use client';
import { useSearchParams } from 'next/navigation';
import React, { useCallback } from 'react';

export const useUrlParams = <T extends Record<string, string | null>>(
  defaultParams: T
) => {
  const search = useSearchParams();
  const params = React.useMemo(() => {
    const urlParams = new URLSearchParams(search);

    return Array.from(urlParams.entries())
      .map(([key, value]) => {
        const currentValue = urlParams.get(key);
        return [key, currentValue ?? value];
      })
      .reduce(
        (result, [key, value]) => ({ ...result, [key as string]: value }),
        defaultParams
      );
  }, [search, defaultParams]);

  const changeParams = React.useCallback(
    (setState: (current: T) => T) => {
      const nextParams = setState(params);
      const nextUrlParams = new URLSearchParams();
      for (const [key, value] of Object.entries(nextParams)) {
        if (typeof value === 'string') {
          nextUrlParams.set(key, value);
          continue;
        }
        if (value == null) {
          nextUrlParams.delete(key);
        }
      }
      window.history.pushState(null, '', `?${nextUrlParams.toString()}`);
    },
    [params, history]
  );

  return { params, changeParams } as const;
};

export const useUrlOpenState = <T extends Record<string, 'open' | null>>({
  defaultValue,
  paramName,
}: {
  defaultValue: T;
  paramName: keyof T;
}) => {
  const { params: stepupModalParams, changeParams: changeStepupModalParams } =
    useUrlParams(defaultValue);
  const open = stepupModalParams?.[paramName] === 'open';
  const setOpen = useCallback(
    (valuer: boolean | ((current: boolean) => boolean)) => {
      changeStepupModalParams((currentParams) => {
        const currentOpen = currentParams?.[paramName] === 'open';
        const nextValue =
          typeof valuer === 'function' ? valuer(currentOpen) : valuer;
        return {
          ...currentParams,
          [paramName]: nextValue ? 'open' : null,
        };
      });
    },
    [paramName, changeStepupModalParams]
  );

  return [open, setOpen] as const;
};
